import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useInView } from 'react-intersection-observer';

import { getFilteredProducts, getSortedProducts } from './utils';

export function useCollectionProducts(
  { activeFilters, id, pagination, products, promoTiles } = {
    activeFilters: {},
    id: '',
    pagination: {},
    products: [],
    promoTiles: [],
  }
) {
  const asPathRef = useRef(null);
  const { asPath } = useRouter();
  const { ref: loadMoreRef, inView } = useInView({
    rootMargin: '600px',
    triggerOnce: false,
  });

  const [filteredProducts, setFilteredProducts] = useState(products);
  const [productsLimit, setProductsLimit] = useState(filteredProducts?.length);
  const [selectedSort, setSelectedSort] = useState(null);

  const enabledPagination = pagination?.enabled || true;
  const resultsPerPage = pagination?.resultsPerPage || 24;
  const isInfiniteScroll =
    pagination?.enabled && pagination?.loadType === 'infinite';

  const selectSort = useCallback((sort) => {
    setSelectedSort(sort || null);
  }, []);

  const loadMoreProducts = useCallback(() => {
    if (productsLimit >= filteredProducts?.length) return;
    const numOfPromoTilesInLoad = promoTiles?.reduce((acc, { position }) => {
      const index = position - 1;
      if (index >= productsLimit && index < productsLimit + resultsPerPage) {
        return acc + 1;
      }
      return acc;
    }, 0);
    setProductsLimit(
      productsLimit + resultsPerPage - (numOfPromoTilesInLoad || 0)
    );
  }, [filteredProducts?.length, productsLimit, promoTiles, resultsPerPage]);

  // sets products limit on collection cms changes
  useEffect(() => {
    if (!enabledPagination || !resultsPerPage) {
      setProductsLimit(filteredProducts?.length);
      return undefined;
    }
    if (!promoTiles) {
      setProductsLimit(resultsPerPage);
      return undefined;
    }

    const numOfPromoTilesInLoad = promoTiles.reduce((acc, { position }) => {
      const index = position - 1;
      if (asPath !== asPathRef.current && index < resultsPerPage) {
        return acc + 1;
      }
      if (index >= productsLimit && index < productsLimit + resultsPerPage) {
        return acc + 1;
      }
      return acc;
    }, 0);
    setProductsLimit(resultsPerPage - (numOfPromoTilesInLoad || 0));
    asPathRef.current = asPath;
    return () => {
      asPathRef.current = null;
    };
  }, [asPath, enabledPagination, resultsPerPage, promoTiles]);

  // filters and sorts products
  useEffect(() => {
    const _filteredProducts = getFilteredProducts({
      products,
      activeFilters,
    });
    const sortedProducts = getSortedProducts({
      products: _filteredProducts,
      sortBy: selectedSort?.value,
    });
    setFilteredProducts(sortedProducts);
  }, [
    activeFilters,
    enabledPagination,
    id,
    resultsPerPage,
    selectedSort?.value,
    products
  ]);

  // auto loads more products if infinite scroll is enabled
  useEffect(() => {
    if (!inView || !isInfiniteScroll) return;
    loadMoreProducts();
  }, [inView, isInfiniteScroll]);

  return {
    state: {
      filteredProducts,
      isInfiniteScroll,
      productsLimit,
      selectedSort,
    },
    actions: {
      loadMoreProducts,
      selectSort,
    },
    refs: {
      loadMoreRef,
    },
  };
}
